import React, { forwardRef } from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'outlined' | 'flat';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  hover?: boolean;
  interactive?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'default',
      padding = 'md',
      rounded = 'md',
      shadow = 'md',
      hover = false,
      interactive = false,
      header,
      footer,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = 'transition-all duration-300';
    const baseStyles = {
      fontFamily: 'var(--patreon-font-primary)',
      transition: `all var(--patreon-animation-duration-normal) var(--patreon-ease-out)`,
      willChange: hover || interactive ? 'transform, box-shadow' : 'auto',
    };

    const variantClasses = {
      default: 'bg-[color:var(--patreon-card-bg)] border border-[color:var(--patreon-card-border)]',
      elevated: 'bg-[color:var(--patreon-card-bg)] border border-[color:var(--patreon-card-border)]',
      outlined: 'bg-[color:var(--patreon-card-bg)] border-2 border-[color:var(--patreon-card-border)]',
      flat: 'bg-[color:var(--patreon-card-bg)] border-0',
    } as const;

    const shadowStyles = {
      default: { boxShadow: 'var(--patreon-card-shadow)' },
      elevated: { boxShadow: 'var(--patreon-card-hover-shadow)' },
      outlined: { boxShadow: 'none' },
      flat: { boxShadow: 'none' },
    };

    const paddingClasses = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
      xl: 'p-10',
    };

    const roundedClasses = {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-[var(--patreon-radius-base)]',
      lg: 'rounded-[var(--patreon-radius-lg)]',
      xl: 'rounded-[var(--patreon-radius-2xl)]',
      full: 'rounded-full',
    };

    const shadowClasses = {
      none: '',
      sm: 'shadow-[var(--patreon-shadow-sm)]',
      md: 'shadow-[var(--patreon-shadow-md)]',
      lg: 'shadow-[var(--patreon-shadow-lg)]',
      xl: 'shadow-[var(--patreon-shadow-xl)]',
    };

    const hoverClasses = hover ? 'hover:shadow-[var(--patreon-shadow-soft-xl)] hover:-translate-y-1' : '';
    const interactiveClasses = interactive ? 'cursor-pointer hover:shadow-[var(--patreon-shadow-soft-lg)] hover:-translate-y-0.5 active:scale-[0.99]' : '';

    const finalClassName = `
      ${baseClasses}
      ${variantClasses[variant]}
      ${paddingClasses[padding]}
      ${roundedClasses[rounded]}
      ${shadowClasses[shadow]}
      ${hoverClasses}
      ${interactiveClasses}
      ${className}
    `.trim();

    return (
      <div
        ref={ref}
        className={finalClassName}
        style={{
          ...baseStyles,
          ...shadowStyles[variant],
        }}
        {...props}
      >
        {header && (
          <div
            className="border-b border-[color:var(--patreon-card-border)] pb-4 mb-4"
            style={{
              borderColor: 'var(--patreon-card-border)',
            }}
          >
            {header}
          </div>
        )}

        {children}

        {footer && (
          <div
            className="border-t border-[color:var(--patreon-card-border)] pt-4 mt-4"
            style={{
              borderColor: 'var(--patreon-card-border)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card sub-components for better structure
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ children, title, subtitle, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`border-b border-[color:var(--patreon-card-border)] pb-4 mb-4 ${className}`}
        style={{
          borderColor: 'var(--patreon-card-border)',
          fontFamily: 'var(--patreon-font-primary)',
        }}
        {...props}
      >
        {title && (
          <div className="space-y-1">
            <h3
              className="font-patreon-semibold"
              style={{
                fontFamily: 'var(--patreon-font-primary)',
                fontSize: 'var(--patreon-font-size-label-3)',
                fontWeight: 'var(--patreon-font-weight-semi-bold)',
                color: 'var(--patreon-text-primary)',
              }}
            >
              {title}
            </h3>
            {subtitle && (
              <p
                className="text-[color:var(--patreon-text-secondary)]"
                style={{
                  fontFamily: 'var(--patreon-font-primary)',
                  fontSize: 'var(--patreon-font-size-label-1)',
                  fontWeight: 'var(--patreon-font-weight-normal)',
                  color: 'var(--patreon-text-secondary)',
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children && !title && children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={className}
        style={{
          fontFamily: 'var(--patreon-font-primary)',
          fontSize: 'var(--patreon-font-size-body)',
          lineHeight: 'var(--patreon-line-height-text)',
          color: 'var(--patreon-text-primary)',
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`border-t border-[color:var(--patreon-card-border)] pt-4 mt-4 ${className}`}
        style={{
          borderColor: 'var(--patreon-card-border)',
          fontFamily: 'var(--patreon-font-primary)',
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// Card action area for clickable cards
interface CardActionAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  onClick?: () => void;
}

const CardActionArea = forwardRef<HTMLDivElement, CardActionAreaProps>(
  ({ children, onClick, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg ${className}`}
        style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        tabIndex={0}
        role="button"
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardActionArea.displayName = 'CardActionArea';

export { Card, CardHeader, CardContent, CardFooter, CardActionArea };
export default Card;
export type { CardProps, CardHeaderProps, CardContentProps, CardFooterProps, CardActionAreaProps };
